import { useEffect, useMemo } from 'react';

import { useCustomerMarketAccounts } from '@src/providers';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import {
  EMPTY_ARRAY,
  Flex,
  FormTable,
  IndicatorBadge,
  MarketAccountStatusEnum,
  VStack,
  useCallbackRef,
  useConstant,
  useFormTable,
  type AgGridMultiSelectDropdownProps,
  type Column,
} from '@talos/kyoko';
import type {
  EditableCallbackParams,
  ICellRendererParams,
  IRowNode,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-enterprise';
import type { CustomCellEditorProps } from 'ag-grid-react';
import { deleteApiKeyAccount, setApiKeyAccountPermissions } from '../state/apiKeys/apiKeysSlice';
import {
  objectFiltersToAccountPermissions,
  type ApiKeyAccountPermissions,
  type ObjectFilterPermissionType,
} from './objectFilters';

const permissionDisplayNames = new Map([
  ['read', 'View'],
  ['write', 'Trade'],
  ['admin', 'Admin'],
]);

function toPermissionsKey(permissions: ObjectFilterPermissionType[]) {
  return permissions.toSorted().join(',');
}

export function ApiKeyAccountFiltersFormTable({
  apiKeyId,
  getAllowedPermissions,
}: {
  apiKeyId: string;
  getAllowedPermissions: (customerAccount: string) => ObjectFilterPermissionType[];
}) {
  const dispatch = useAppStateDispatch();
  const { customerMarketAccountsByName } = useCustomerMarketAccounts();

  const objectFilters = useAppStateSelector(
    state => state.customerUserDrawer.apiKeys.keys?.find(key => key.CustomerUserApiKeyID === apiKeyId)?.ObjectFilters
  );
  const savedObjectFilters = useAppStateSelector(
    state => state.customerUserDrawer.apiKeys.savedObjectFiltersByKeyId[apiKeyId]
  );
  const deletedAccounts = useAppStateSelector(
    state => state.customerUserDrawer.apiKeys.deletedAccountsByKeyId[apiKeyId] ?? EMPTY_ARRAY
  );

  const accountPermissions = useMemo(() => objectFiltersToAccountPermissions(objectFilters), [objectFilters]);

  const dirtyRowIds = useMemo(() => {
    const savedPermissionsByAccount = new Map(
      objectFiltersToAccountPermissions(savedObjectFilters).map(account => [
        account.customerAccount,
        toPermissionsKey(account.permissions),
      ])
    );
    return accountPermissions
      .filter(
        account =>
          !deletedAccounts.includes(account.customerAccount) &&
          savedPermissionsByAccount.get(account.customerAccount) !== toPermissionsKey(account.permissions)
      )
      .map(account => account.customerAccount);
  }, [accountPermissions, savedObjectFilters, deletedAccounts]);

  // Columns are created once via useConstant; these keep a stable identity while always reading latest state.
  const getAccountLabel = useCallbackRef(
    (customerAccount: string) => customerMarketAccountsByName.get(customerAccount)?.SourceAccountID ?? customerAccount
  );

  const resolvePermissionOptions = useCallbackRef(getAllowedPermissions);

  // Inserts for inactive accounts are rejected by the API; those rows stay read-only so they can only be removed.
  const isAccountEditable = useCallbackRef(
    (customerAccount: string) =>
      customerMarketAccountsByName.get(customerAccount)?.Status === MarketAccountStatusEnum.Active
  );

  const updatePermissions = useCallbackRef((customerAccount: string, permissions: ObjectFilterPermissionType[]) => {
    dispatch(
      setApiKeyAccountPermissions({
        apiKeyId,
        accountPermissions: accountPermissions.map(account =>
          account.customerAccount === customerAccount ? { ...account, permissions } : account
        ),
      })
    );
  });

  const removeAccount = useCallbackRef((customerAccount: string) => {
    dispatch(deleteApiKeyAccount({ apiKeyId, customerAccount }));
  });

  const columns = useConstant<Column[]>([
    {
      type: 'custom',
      field: 'customerAccount',
      title: 'Account',
      flex: 1,
      params: {
        valueFormatter: ({ value }: ValueFormatterParams<ApiKeyAccountPermissions, string>) =>
          value ? getAccountLabel(value) : '',
      },
    },
    {
      type: 'custom',
      title: 'Permissions',
      width: 200,
      id: 'permission',
      params: {
        editable: ({ data }: EditableCallbackParams<ApiKeyAccountPermissions>) =>
          data ? isAccountEditable(data.customerAccount) : false,
        suppressKeyboardEvent: () => true,
        cellEditor: 'multiSelectDropdown',
        cellEditorPopup: true,
        cellEditorParams: (params: CustomCellEditorProps<ApiKeyAccountPermissions>) => {
          return {
            ...params,
            multiSelectProps: {
              options: params.data ? resolvePermissionOptions(params.data.customerAccount) : [],
              getLabel: permission => permissionDisplayNames.get(permission) || permission,
            },
          } satisfies AgGridMultiSelectDropdownProps<string>;
        },
        valueGetter: ({ data }: ValueGetterParams<ApiKeyAccountPermissions>) => data?.permissions ?? [],
        valueSetter: ({
          newValue,
          data,
        }: ValueSetterParams<ApiKeyAccountPermissions, ObjectFilterPermissionType[]>) => {
          data && updatePermissions(data.customerAccount, newValue ?? []);
        },
        cellRenderer: ({ value }: ICellRendererParams<ApiKeyAccountPermissions, ObjectFilterPermissionType[]>) => (
          <Flex gap="spacing4" alignItems="center">
            {(value ?? []).map(permission => (
              <IndicatorBadge key={permission} children={permissionDisplayNames.get(permission) || permission} />
            ))}
          </Flex>
        ),
      },
    },
    {
      type: 'remove',
      id: 'remove',
      params: {
        onClick: ({ node }: { node: IRowNode<ApiKeyAccountPermissions> }) => {
          node.data && removeAccount(node.data.customerAccount);
        },
      },
    },
  ]);

  const formTable = useFormTable<ApiKeyAccountPermissions>({
    rowID: 'customerAccount',
    data: accountPermissions,
    dirtyRowIds,
    deletedRowIds: deletedAccounts,
    columns,
  });

  const { reset } = formTable;
  useEffect(() => {
    reset(accountPermissions, dirtyRowIds, deletedAccounts);
  }, [reset, accountPermissions, dirtyRowIds, deletedAccounts]);

  return (
    <VStack alignItems="stretch" h="100%" data-testid="api-key-account-filters-table">
      <FormTable {...formTable} />
    </VStack>
  );
}
